import FmdBadIcon from '@mui/icons-material/FmdBad';
import {
  Alert, Box, Button, Typography,
} from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Wysiwyg from '../../../../../../../../../../Common/RichTextRenderer/Wysiwyg';
import { SessionSummaryFieldsFragment, useUpdateSessionNoteMutation } from '../../../../../../../../../../generated/core-api/graphql-react-query';
import defaultRichText from '../../../../../../../../../../utils/defaultRichText';
import { useOrganisationData } from '../../../../../../../../../Hooks/useOrganisation';
import { useUserInfoData } from '../../../../../../../../../Hooks/useUserInfo';
import StyledBox from './styles';
import useUpdateSession from './useUpdateSession';

interface SessionNoteProps {
  session: SessionSummaryFieldsFragment;
}

function usePlaceholder(you: boolean, editable: boolean, name: string) {
  const { t } = useTranslation('session');
  if (!editable) {
    if (you) return t('details.note.placeholder-you');
    return t('details.note.placeholder-others', { name });
  }
  return t('details.note.placeholder');
}

export default function SessionNote(props: SessionNoteProps) {
  const { session } = props;
  const orgInfo = useOrganisationData();
  const { t } = useTranslation('session');
  const { t: tCommon } = useTranslation('common');
  const [noteBuffer, setNoteBuffer] = React.useState<string>(session.note ?? JSON.stringify(defaultRichText));
  const [edit, setEdit] = React.useState(false);
  const userInfo = useUserInfoData();
  const placeholder = usePlaceholder(session.user === userInfo.id, edit, session.userField.displayName);
  const updateSession = useUpdateSession();
  const updateNote = useUpdateSessionNoteMutation({
    onSuccess(data) {
      updateSession(data.session.updateNote);
      setEdit(false);
    },
  });

  const disabled = userInfo.id !== session.user;

  React.useEffect(() => {
    if (session.note) {
      setNoteBuffer(session.note);
    } else {
      setNoteBuffer(JSON.stringify(defaultRichText));
    }
  }, [session.note]);

  if (!orgInfo.features.cogneedAi || orgInfo.features.cogneedAi?.disableSessionNotes) return null;

  return (
    <StyledBox>
      <>
        <Typography variant="subtitle1">
          {t('details.note.title')}
        </Typography>
        <Alert icon={<FmdBadIcon fontSize="small" />} severity="warning">
          {t('details.note.warning')}
        </Alert>
        <Box className={clsx('richText', { disabled: (disabled || !edit) })}>
          <Wysiwyg
            disabled={disabled || !edit}
            initValue={noteBuffer}
            onChange={(e) => { if (noteBuffer !== e) setNoteBuffer((prev) => (prev !== e ? e : prev)); }}
            placeholder={placeholder}
          />
          {!disabled && (
            <Box className="actions">
              {edit ? (
                <>
                  <Button
                    variant="contained"
                    disabled={session.note === noteBuffer || !noteBuffer || noteBuffer === JSON.stringify(defaultRichText)}
                    onClick={() => {
                      updateNote.mutate({
                        input: {
                          id: session.id,
                          note: noteBuffer,
                        },
                      });
                    }}
                  >
                    {tCommon('save')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEdit(false);
                      setNoteBuffer(session.note ?? JSON.stringify(defaultRichText));
                    }}
                  >
                    {tCommon('cancel')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    title={noteBuffer !== JSON.stringify(defaultRichText) ? tCommon('edit') : tCommon('add')}
                    disabled={disabled}
                    variant="text"
                    onClick={() => { setEdit(true); }}
                  >
                    {noteBuffer !== JSON.stringify(defaultRichText) ? tCommon('edit') : tCommon('add')}
                  </Button>
                  {noteBuffer !== JSON.stringify(defaultRichText) && (
                    <Button
                      title={tCommon('delete')}
                      disabled={disabled}
                      variant="text"
                      onClick={() => {
                        updateNote.mutate({
                          input: {
                            id: session.id,
                            note: null,
                          },
                        });
                        setNoteBuffer(JSON.stringify(defaultRichText));
                      }}
                    >
                      {tCommon('delete')}
                    </Button>
                  )}
                </>
              )}
            </Box>
          )}

        </Box>
      </>

    </StyledBox>
  );
}
